import { TFunction } from "i18next";

import { formatDecimal, formatEuro } from "../../../../../../format/formatNumber";
import {
  SeizableIncomeAssessmentResponse,
  SeizableIncomeExplanationKind,
  SeizableIncomeExplanationLineResponse,
} from "../../../../../api/ClientFileApi";

const SECTION = "clients.client.debtSettlement.assetInventory.incomeTab.seizableAmount";
const EXPLANATION = `${SECTION}.explanation`;

const FULL_PERCENT = 100;
const FOURTEEN_TIMES = 14;

export function formatPercent(value: number, locale: string): string {
  const decimals = Number.isInteger(value) ? 0 : 2;
  return `${formatDecimal(value, locale, decimals)} %`;
}

export function resultText(assessment: SeizableIncomeAssessmentResponse, t: TFunction, locale: string): string {
  const euro = (value: number) => formatEuro(value, locale);
  const { seizableAmount12, seizableAmount14, income12, income14, maintenanceObligationCount } = assessment;

  if (seizableAmount12 > 0 && seizableAmount14 > 0) {
    return t(`${SECTION}.result.bothSeizable`, { amount12: euro(seizableAmount12), amount14: euro(seizableAmount14) });
  }
  if (seizableAmount12 > 0) {
    return t(`${SECTION}.result.seizable12`, { amount: euro(seizableAmount12) });
  }
  if (seizableAmount14 > 0) {
    return t(`${SECTION}.result.seizable14`, { amount: euro(seizableAmount14) });
  }
  if (income12 === 0 && income14 === 0) {
    return t(`${SECTION}.result.nothingSeizableAtTotal`, { total: euro(assessment.nonSeizableIncome) });
  }
  return t(`${SECTION}.result.nothingSeizable`, { count: maintenanceObligationCount });
}

export function explanationText(line: SeizableIncomeExplanationLineResponse, t: TFunction, locale: string): string {
  const euro = (value: number | null | undefined) => formatEuro(value ?? 0, locale);
  const percent = (value: number | null | undefined) => formatPercent(value ?? 0, locale);
  const times = line.paymentsPerYear ?? 0;

  switch (line.kind) {
    case SeizableIncomeExplanationKind.NoIncome:
      return t(`${EXPLANATION}.noIncome`);
    case SeizableIncomeExplanationKind.SingleStream:
      return times === FOURTEEN_TIMES
        ? t(`${EXPLANATION}.singleStream14`, {
            income: euro(line.income),
            count: line.maintenanceObligationCount ?? 0,
            seizable: euro(line.seizableAmount),
          })
        : t(`${EXPLANATION}.singleStream12`, {
            income: euro(line.income),
            count: line.maintenanceObligationCount ?? 0,
            seizable: euro(line.seizableAmount),
          });
    case SeizableIncomeExplanationKind.StreamSeizable:
      return t(`${EXPLANATION}.streamSeizable`, {
        income: euro(line.income),
        times,
        seizable: euro(line.seizableAmount),
      });
    case SeizableIncomeExplanationKind.StreamSeizableWithPercentage:
      return t(`${EXPLANATION}.streamSeizableWithPercentage`, {
        income: euro(line.income),
        times,
        seizable: euro(line.seizableAmount),
        percentage: percent(line.percentage),
      });
    case SeizableIncomeExplanationKind.StreamFullyProtected:
      return t(`${EXPLANATION}.streamFullyProtected`, {
        income: euro(line.income),
        times,
        base: euro(line.baseAmount),
      });
    case SeizableIncomeExplanationKind.OtherStreamAlsoProtected:
      return t(`${EXPLANATION}.otherStreamAlsoProtected`, {
        income: euro(line.income),
        times,
        combined: euro((line.income ?? 0) + (line.otherIncome ?? 0)),
        base: euro(line.baseAmount),
      });
    case SeizableIncomeExplanationKind.OtherStreamSeizableAfterDifference:
      return t(`${EXPLANATION}.otherStreamSeizableAfterDifference`, {
        income: euro(line.income),
        times,
        seizable: euro(line.seizableAmount),
        percentage: percent(line.percentage),
        difference: euro(line.difference),
        otherIncome: euro(line.otherIncome),
        base: euro(line.baseAmount),
      });
    case SeizableIncomeExplanationKind.AliquotSplit:
      return t(`${EXPLANATION}.aliquotSplit`, {
        share12: percent(line.share),
        income12: euro(line.income),
        share14: percent(FULL_PERCENT - (line.share ?? 0)),
        income14: euro(line.otherIncome),
      });
    case SeizableIncomeExplanationKind.AliquotStreamProtected:
      return t(`${EXPLANATION}.aliquotStreamProtected`, {
        income: euro(line.income),
        times,
        share: percent(line.share),
        subsistence: euro(line.subsistenceAmount),
        base: euro(line.baseAmount),
      });
    case SeizableIncomeExplanationKind.AliquotFullyProtected:
      return t(`${EXPLANATION}.aliquotFullyProtected`, {
        income12: euro(line.income),
        income14: euro(line.otherIncome),
      });
  }
}
